import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageCircle, Clock, Bell, Sparkles } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Grid, VStack, HStack } from "@/components/core";

interface FollowUpSettings {
  enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  preferred_contexts: string[];
  frequency: 'rare' | 'normal' | 'frequent';
  max_per_day: number;
}

const contextOptions = [
  { id: 'good_morning', label: 'Guten Morgen Nachrichten', icon: '☀️' },
  { id: 'good_night', label: 'Gute Nacht Nachrichten', icon: '🌙' },
  { id: 'motivational', label: 'Motivierende Nachrichten', icon: '💪' },
  { id: 'flirty', label: 'Flirtende Nachrichten', icon: '😘' },
  { id: 'check_in', label: 'Check-in Nachrichten', icon: '👋' },
  { id: 'playful', label: 'Verspielte Nachrichten', icon: '🎮' },
  { id: 'romantic', label: 'Romantische Nachrichten', icon: '💝' },
  { id: 'supportive', label: 'Unterstützende Nachrichten', icon: '🤗' },
];

const frequencyOptions = [
  { value: 'rare', label: 'Selten', description: 'Max. 1-2 pro Tag', maxPerDay: 2 },
  { value: 'normal', label: 'Normal', description: 'Max. 3-5 pro Tag', maxPerDay: 5 },
  { value: 'frequent', label: 'Oft', description: 'Max. 8-10 pro Tag', maxPerDay: 10 },
];

const FollowUpSettingsComponent = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<FollowUpSettings>({
    enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    preferred_contexts: ['good_morning', 'check_in'],
    frequency: 'normal',
    max_per_day: 5,
  });

  useEffect(() => {
    void fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/v1/user/follow-up-settings', {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else if (response.status === 404) {
        // No settings exist yet, use defaults
        console.log('No follow-up settings found, using defaults');
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching follow-up settings:', error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht geladen werden",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/v1/user/follow-up-settings', {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      toast({
        title: "Erfolgreich",
        description: "Follow-Up Einstellungen wurden gespeichert",
      });
    } catch (error) {
      console.error('Error saving follow-up settings:', error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht gespeichert werden",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleContext = (contextId: string) => {
    setSettings(prev => ({
      ...prev,
      preferred_contexts: prev.preferred_contexts.includes(contextId)
        ? prev.preferred_contexts.filter(id => id !== contextId)
        : [...prev.preferred_contexts, contextId]
    }));
  };

  const handleFrequencyChange = (value: string) => {
    const frequency = value as 'rare' | 'normal' | 'frequent';
    const maxPerDay = frequencyOptions.find(opt => opt.value === frequency)?.maxPerDay || 5;
    setSettings(prev => ({
      ...prev,
      frequency,
      max_per_day: maxPerDay,
    }));
  };

  if (loading) {
    return (
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Skeleton className="h-5 w-5 mr-token-sm rounded bg-white/10" />
            <Skeleton className="h-6 w-48 bg-white/10" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-token-lg">
          {/* Enable/Disable Toggle Skeleton */}
          <HStack align="center" justify="between" className="p-token-md rounded-token-button bg-white/5 border border-white/10">
            <HStack align="center" gap="sm">
              <Skeleton className="h-6 w-6 rounded bg-white/10" />
              <div className="space-y-token-sm">
                <Skeleton className="h-5 w-56 bg-white/10" />
                <Skeleton className="h-4 w-72 bg-white/10" />
              </div>
            </HStack>
            <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
          </HStack>

          {/* Quiet Hours Skeleton */}
          <div className="space-y-token-md">
            <HStack align="center" gap="sm">
              <Skeleton className="h-5 w-5 rounded bg-white/10" />
              <Skeleton className="h-6 w-24 bg-white/10" />
            </HStack>
            <Grid cols={2} gap="md">
              <VStack gap="sm">
                <Skeleton className="h-4 w-32 bg-white/10" />
                <Skeleton className="h-10 w-full rounded bg-white/10" />
              </VStack>
              <VStack gap="sm">
                <Skeleton className="h-4 w-28 bg-white/10" />
                <Skeleton className="h-10 w-full rounded bg-white/10" />
              </VStack>
            </Grid>
            <Skeleton className="h-4 w-80 bg-white/10" />
          </div>

          {/* Frequency Radio Group Skeleton */}
          <div className="space-y-token-md">
            <HStack align="center" gap="sm">
              <Skeleton className="h-5 w-5 rounded bg-white/10" />
              <Skeleton className="h-6 w-24 bg-white/10" />
            </HStack>
            <VStack gap="sm">
              {[1, 2, 3].map((i) => (
                <HStack key={i} align="center" gap="sm" className="p-token-sm rounded-token-button">
                  <Skeleton className="h-4 w-4 rounded-full bg-white/10" />
                  <VStack gap="xs" className="flex-1">
                    <Skeleton className="h-5 w-20 bg-white/10" />
                    <Skeleton className="h-4 w-32 bg-white/10" />
                  </VStack>
                </HStack>
              ))}
            </VStack>
          </div>

          {/* Context Checkboxes Grid Skeleton */}
          <VStack gap="md">
            <Skeleton className="h-6 w-52 bg-white/10" />
            <Grid cols={1} colsMd={2} gap="sm">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <HStack key={i} gap="sm" align="center" className="p-token-sm rounded-token-button bg-white/5">
                  <Skeleton className="h-4 w-4 rounded bg-white/10" />
                  <HStack gap="sm" align="center" className="flex-1">
                    <Skeleton className="h-6 w-6 rounded bg-white/10" />
                    <Skeleton className="h-4 w-36 bg-white/10" />
                  </HStack>
                </HStack>
              ))}
            </Grid>
            <Skeleton className="h-4 w-72 bg-white/10" />
          </VStack>

          {/* Save Button Skeleton */}
          <div className="pt-token-md">
            <Skeleton className="h-10 w-48 rounded bg-white/10" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-effect">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <MessageCircle className="h-5 w-5 mr-token-sm" />
          Follow-Up Nachrichten
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-token-lg">
        {/* Enable/Disable Toggle */}
        <HStack align="center" justify="between" className="p-token-md rounded-token-button bg-white/5 border border-white/10">
          <HStack align="center" gap="sm">
            <Bell className={`h-6 w-6 ${settings.enabled ? 'text-brand-primary' : 'text-muted-foreground'}`} />
            <div>
              <h3 className="text-white font-semibold">Follow-Up Nachrichten aktivieren</h3>
              <p className="text-muted-foreground text-sm">
                Erhalte automatische Nachrichten von deinen Charakteren
              </p>
            </div>
          </HStack>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
          />
        </HStack>

        {settings.enabled && (
          <>
            {/* Quiet Hours */}
            <div className="space-y-token-md">
              <HStack align="center" gap="sm">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <Label className="text-white text-lg">Ruhezeiten</Label>
              </HStack>
              <Grid cols={2} gap="md">
                <VStack gap="sm">
                  <Label className="text-muted-foreground/80">Ruhezeit beginnt um</Label>
                  <Input
                    type="time"
                    value={settings.quiet_hours_start}
                    onChange={(e) => setSettings({ ...settings, quiet_hours_start: e.target.value })}
                    className="bg-white/5 border-white/20 text-white"
                  />
                </VStack>
                <VStack gap="sm">
                  <Label className="text-muted-foreground/80">Ruhezeit endet um</Label>
                  <Input
                    type="time"
                    value={settings.quiet_hours_end}
                    onChange={(e) => setSettings({ ...settings, quiet_hours_end: e.target.value })}
                    className="bg-white/5 border-white/20 text-white"
                  />
                </VStack>
              </Grid>
              <p className="text-muted-foreground text-sm">
                Während der Ruhezeiten erhältst du keine Follow-Up Nachrichten
              </p>
            </div>

            {/* Frequency */}
            <div className="space-y-token-md">
              <HStack align="center" gap="sm">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
                <Label className="text-white text-lg">Häufigkeit</Label>
              </HStack>
              <RadioGroup value={settings.frequency} onValueChange={handleFrequencyChange}>
                {frequencyOptions.map((option) => (
                  <HStack
                    key={option.value}
                    align="center"
                    gap="sm"
                    className="p-token-sm rounded-token-button hover:bg-white/5 transition-colors"
                  >
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label
                      htmlFor={option.value}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="text-white font-medium">{option.label}</div>
                      <div className="text-muted-foreground text-sm">{option.description}</div>
                    </Label>
                  </HStack>
                ))}
              </RadioGroup>
            </div>

            {/* Preferred Contexts */}
            <VStack gap="md">
              <Label className="text-white text-lg">Bevorzugte Nachrichtentypen</Label>
              <Grid cols={1} colsMd={2} gap="sm">
                {contextOptions.map((context) => (
                  <HStack
                    key={context.id}
                    gap="sm"
                    align="center"
                    className="p-token-sm rounded-token-button bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => toggleContext(context.id)}
                  >
                    <Checkbox
                      checked={settings.preferred_contexts.includes(context.id)}
                      onCheckedChange={() => toggleContext(context.id)}
                      className="data-[state=checked]:bg-brand-primary data-[state=checked]:border-brand-primary"
                    />
                    <Label className="cursor-pointer flex-1">
                      <HStack align="center" gap="sm">
                        <span className="text-xl">{context.icon}</span>
                        <span className="text-white">{context.label}</span>
                      </HStack>
                    </Label>
                  </HStack>
                ))}
              </Grid>
              <p className="text-muted-foreground text-sm">
                Wähle die Arten von Nachrichten, die du erhalten möchtest
              </p>
            </VStack>
          </>
        )}

        {/* Save Button */}
        <div className="pt-token-md">
          <Button
            onClick={saveSettings}
            disabled={saving || settings.preferred_contexts.length === 0}
            className="bg-gradient-to-r from-brand-secondary to-brand-primary hover:from-brand-secondary-dark hover:to-brand-primary-dark disabled:opacity-50"
          >
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
          {settings.enabled && settings.preferred_contexts.length === 0 && (
            <p className="text-brand-primary text-sm mt-token-sm">
              Bitte wähle mindestens einen Nachrichtentyp aus
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FollowUpSettingsComponent;